<template>

  <ValidationObserver v-slot="{ invalid }">
    <vx-card
      :title="$t('class-over-view.class-assignment')"
      class="w-full mb-5"
      title-color="primary" v-if="classAssignments"
    >
      <template slot="actions">
        <vx-tooltip :text="$t('general.add') + $t('class-over-view.class-assignment')" class="float-right">
          <vs-button radius color="dark" type="flat" icon-pack="feather" icon="icon-plus"
                     @click="onAddClick"></vs-button>
        </vx-tooltip>
      </template>

      <!--EDIT FORM-->
      <div v-if="showEditForm">

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full mb-2">
            <ValidationProvider :name="$t('class-assignment.sequence')" rules="required|numeric" v-slot="{ errors }">
              <vs-input :label="$t('class-assignment.sequence') + ' *'" class="w-full"
                        @input="onSequenceInput()"
                        v-model.number="form.classAssignmentInput.sequence"/>
              <span class="text-danger text-sm" v-show="errors[0]">{{errors[0]}}</span>
            </ValidationProvider>
          </div>

          <div class="vx-col sm:w-1/2 w-full mb-2 pt-6">
            <vs-checkbox v-model="form.classAssignmentInput.active">
              {{$t('class-assignment.active')}}
            </vs-checkbox>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full mb-2">
            <ValidationProvider :name="$t('live-class-schedule.topic-local')" rules="required" v-slot="{ errors }">
              <vs-input :label="$t('live-class-schedule.topic-local') + ' *'" class="w-full"
                        v-model="form.classAssignmentInput.topicLocal"/>
              <span class="text-danger text-sm" v-show="errors[0]">{{errors[0]}}</span>
            </ValidationProvider>
          </div>

          <div class="vx-col sm:w-1/2 w-full mb-2">
            <vs-input :label="$t('live-class-schedule.topic-eng')" class="w-full"
                      v-model="form.classAssignmentInput.topicEng"/>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full">
            <label class="text-sm">{{$t('class-section.description-local')}}</label>
            <vs-textarea class="mb-2" rows="2" v-model="form.classAssignmentInput.descriptionLocal"/>
          </div>
          <div class="vx-col sm:w-1/2 w-full">
            <label class="text-sm">{{$t('class-section.description-eng')}}</label>
            <vs-textarea class="mb-2" rows="2" v-model="form.classAssignmentInput.descriptionEng"/>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col sm:w-1/2 w-full mb-2">
            <ValidationProvider :name="$t('class-over-view.due-date')" rules="required" v-slot="{ errors }">
              <label class="text-sm">{{$t('class-over-view.due-date') + ' *'}}</label>
              <datepicker :format="dateTimeFormat.yyyymmdd"
                          v-model="form.classAssignmentInput.dueDate"
                          ></datepicker>
              <span class="text-danger text-sm" v-show="errors[0]">{{errors[0]}}</span>
            </ValidationProvider>
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-2">
            <ValidationProvider :name="$t('exam-upload.header.score')" rules="required|numeric" v-slot="{ errors }">
              <vs-input :label="$t('exam-upload.header.score') + ' *'" class="w-full"
                        v-model.number="form.classAssignmentInput.score"/>
              <span class="text-danger text-sm" v-show="errors[0]">{{errors[0]}}</span>
            </ValidationProvider>
          </div>

        </div>

        <div class="vx-row">
          <div class="vx-col w-full mb-2">
            <div class="flex items-center">
              <input type="file" class="hidden" ref="uploadIInput"
                     @change="onFilUploadChange"
                     :accept="acceptFileType">
              <vx-tooltip :title="$t('class-assignment.upload')" class="float-left">
                <vs-button color="primary" icon-pack="feather" icon="icon-upload"
                           @click="$refs.uploadIInput.click()"/>
              </vx-tooltip>
              <span class="ml-2">{{ fileUploadName }}</span>
            </div>
          </div>
        </div>

        <!----------------------------------------- Actions Button ---------------------------------------------------->
        <div class="vx-row pt-2">
          <div class="vx-col w-full">
            <vs-button :disabled="local.isProcessing || invalid || isUploading || !canSave"
                       class="mr-2"
                       @click.prevent="onSaveClick">
              {{ $t('general.save') }}
            </vs-button>

            <vs-button type="border" color="warning"
                       :disabled="local.isProcessing || isUploading"
                       @click.prevent="onCancelClick()">
              {{ $t('general.cancel') }}
            </vs-button>
          </div>
        </div>

        <vs-divider/>

      </div>

      <!--LIST ITEM-->
      <div class="block overflow-x-auto">
        <table class="w-full">
          <tr>
            <th class="px-3 py-2">
              <vs-chip transparent color="primary">
                <span>{{ $t('live-class-schedule.topic') }}</span>
              </vs-chip>
            </th>
            <th class="px-3 py-2">
              <vs-chip transparent color="primary">
                <span>{{ $t('exam-upload.header.score') }}</span>
              </vs-chip>
            </th>

            <th class="px-3 py-2">
              <vs-chip transparent color="primary">
                <span>{{ $t('class-over-view.due-date') }}</span>
              </vs-chip>
            </th>
            <th class="font-semibold text-base text-left px-3 py-2" width="90">
              <!--<vs-chip transparent color="primary">
                <span>{{ $t('class-over-view.due-date') }}</span>
              </vs-chip>-->
            </th>
          </tr>

          <tr v-for="item in classAssignments" :key="item.id">
            <td class="px-3 py-2">
              {{ item.sequence }} {{ item.topicLocal }}
              <feather-icon v-if="item.uploadFileName"
                            @click="onDownloadClick(item)" class="ml-1 cursor-pointer"
                            icon="DownloadIcon"
                            svgClasses="w-5 h-5 text-primary stroke-current"/>
              <div class="float-right">
                <feather-icon v-if="item.active"
                              icon="CheckIcon"
                              svgClasses="w-5= h-5 text-success stroke-current"/>
                <feather-icon v-if="!item.active"
                              icon="XIcon"
                              svgClasses="w-5 h-5 text-danger stroke-current"/>
              </div>
              <br>
              {{ $t('course-faq.updated-date') }}
              <br>{{ item.updatedAt | datetime }}
            </td>
            <td class="px-3 py-2 text-right">{{ item.score }}</td>
            <td class="px-3 py-2">
              {{ formatDate(item.dueDate) }}
            </td>
            <td class="px-3 py-2 text-right">
              <feather-icon @click="onEditClick(item)" class="mr-2 cursor-pointer"
                            icon="EditIcon"
                            svgClasses="w-5 h-5 hover:text-primary stroke-current"/>
              <feather-icon @click="onDeleteClick(item)"
                            icon="TrashIcon" class="cursor-pointer"
                            svgClasses="w-5 h-5 hover:text-primary stroke-current"/>
            </td>
          </tr>
        </table>
      </div>

    </vx-card>
  </ValidationObserver>

</template>

<script>
  import ClassAssignment from '@/store/modules/courses/class-assignment/index';
  import {state} from "../data";
  import {showStateNotification, showLocalNotification, notificationType} from "@/utils/notification";
  import {confirmDelete, confirmSave} from "@/utils/dialogs";
  import moment from 'moment';
  import {dateTimeFormat} from "@/utils/format";
  import {config} from "@/utils/upload";
  import Datepicker from 'vuejs-datepicker';
  import {createNamespacedHelpers} from "vuex";
  import {createHelpers} from "vuex-map-fields";
  import {getECode, getRStage, getToken} from "@/utils/auth-data";

  if (!state.store.ClassAssignment) state.registerModule(`ClassAssignment`, ClassAssignment);

  const {
    mapActions: mapClassAssignmentActions,
  } = createNamespacedHelpers(`ClassAssignment`);


  const {
    mapFields: mapClassAssignmentFields,
  } = createHelpers({
    getterType: `ClassAssignment/getField`,
    mutationType: `ClassAssignment/updateField`,
  });

  export default {
    props: {
      classRoom: Object
    },
    components:{
      Datepicker,
    },
    data() {
      return {
        local: state.local,

        dateTimeFormat: dateTimeFormat,
        showEditForm: false,
        isUploading: false,
        acceptFileType: config.fileExtension.document,
        form: {
          id: '',
          classAssignmentInput: {
            sectionNo: null,
            sequence: null,
            topicLocal: null,
            topicEng: null,
            descriptionLocal: null,
            descriptionEng: null,
            score: 0,
            dueDate: null,
            uploadPath: null,
            uploadPathUri: null,
            uploadFileName: null,
            active: true,
          }
        },

        fileUpload: null
      }
    },
    computed: {
      ...mapClassAssignmentFields({classAssignmentProcessing: 'processing',classAssignmentSuccess: 'success',classAssignmentError: 'error',}),
      classAssignments() {
        return this.classRoom ? this.classRoom.classSection.classAssignments : [];
      },
      showFileUpload(){
        return !!this.form.id;
      },
      fileUploadName(){
        return this.fileUpload ? this.fileUpload.name:'';
      },
      canSave(){
        if(this.form.id){
          return true
        }else if(!this.fileUpload){
          return false
        }
        return true
      }
    },
    watch: {
      classAssignmentProcessing(data) {
        this.local.isProcessing = data;
      },
      classAssignmentSuccess(data) {
        showStateNotification(this.$vs, data);
      },
      classAssignmentError(data) {
        showStateNotification(this.$vs, data);
      }
    },
    methods: {
      //---------------------------------------- State Function ------------------------------------------------------//
      ...mapClassAssignmentActions({
        getClassAssignment: 'getClassAssignment',
        checkCanDelete: 'checkCanDelete',
        addClassAssignment: 'addClassAssignment',
        updateClassAssignment: 'updateClassAssignment',
        deleteClassAssignment: 'deleteClassAssignment',
        uploadAssignment: 'uploadAssignment',
      }),
      //---------------------------------------- Event Function ------------------------------------------------------//
      onDownloadClick(item) {
        let url = config.url.downloadFile + item.uploadPathUri;

        window.open(url, '_blank');
      },
      onAddClick(){
        if(this.showEditForm === true){
          this.showEditForm = false
        }else{
          this.form.id = null;
          this.form.classAssignmentInput.sectionNo = this.classRoom.sectionNo;
          this.form.classAssignmentInput.sequence = this.classAssignments.length + 1;
          this.form.classAssignmentInput.topicLocal = null;
          this.form.classAssignmentInput.topicEng = null;
          this.form.classAssignmentInput.descriptionLocal = null;
          this.form.classAssignmentInput.descriptionEng = null;
          this.form.classAssignmentInput.score = 0;
          this.form.classAssignmentInput.active = true;
          this.form.classAssignmentInput.dueDate = null;
          // this.form.classAssignmentInput.uploadPath = null;
          // this.form.classAssignmentInput.uploadPathUri = null;
          this.form.classAssignmentInput.uploadFileName = null;

          this.fileUpload = null;
          this.showEditForm = true;
        }
      },
      onEditClick(tr) {
        this.form.id = tr.id;
        this.form.classAssignmentInput.sectionNo = tr.sectionNo;
        this.form.classAssignmentInput.sequence = tr.sequence;
        this.form.classAssignmentInput.topicLocal = tr.topicLocal;
        this.form.classAssignmentInput.topicEng = tr.topicEng;
        this.form.classAssignmentInput.descriptionLocal = tr.descriptionLocal;
        this.form.classAssignmentInput.descriptionEng = tr.descriptionEng;
        this.form.classAssignmentInput.score = tr.score;
        this.form.classAssignmentInput.dueDate = tr.dueDate;
        this.form.classAssignmentInput.active = tr.active;
        this.form.classAssignmentInput.uploadPath = tr.uploadPath;
        this.form.classAssignmentInput.uploadPathUri = tr.uploadPathUri;
        this.form.classAssignmentInput.uploadFileName = tr.uploadFileName;

        this.fileUpload = null;
        this.showEditForm = true;
      },
      onCancelClick(){
        this.showEditForm = false;
      },
      async onDeleteClick(tr){
        await confirmDelete(this.$vs, this.removeClassAssignment, {id: tr.id});
      },
      async onSaveClick(){
        let payload = {
          classAssignmentInput: {
            ...this.form.classAssignmentInput
          },
        }
        if(!this.form.id){
          //insert
          payload['classSectionId'] =  this.classRoom.classSection.id;
        }else{
          //update
          payload['id'] =  this.form.id;
        }

        await confirmSave(this.$vs, this.saveClassAssignment, payload);
      },
      async onFilUploadChange(e){
        if(e.target.files && e.target.files.length > 0){
          this.fileUpload = e.target.files[0];

          this.form.classAssignmentInput.uploadFileName = e.target.files[0].name;

        }else{
          this.fileUpload = null;
          this.form.classAssignmentInput.uploadFileName = '';
        }
      },
      onSequenceInput(){
        const sequence = this.classAssignments.find(e => e.sequence === this.form.classAssignmentInput.sequence
        && e.id !== this.form.classAssignmentInput.id)
        if(sequence){
          this.form.classAssignmentInput.sequence = null
        }
      },
      //---------------------------------------- Local Function ------------------------------------------------------//
      async saveClassAssignment(payload){
        let result;
        if(this.fileUpload){
          //if have fileUpload then upload file
          let uploadPayload = {
            file: this.fileUpload,
            file1: this.form.classAssignmentInput.uploadFileName,
            operationName: config.operationName.classAssignment,
            stage: getRStage,
            entity: getECode,
            authorization: getToken,
            app: "teacher"

          }
          let resultUpload = await this.uploadAssignment(uploadPayload);
          if(resultUpload){
            payload.classAssignmentInput['uploadPath'] = resultUpload.file.uuid;
            payload.classAssignmentInput['uploadPathUri'] = resultUpload.file.uuid;
          }
        }

        if (!payload.id){
          result = await this.addClassAssignment(payload);
        } else {
          result = await this.updateClassAssignment(payload);
        }

        if(result){

          // update list
          result = JSON.parse(JSON.stringify(result));
          if (!payload.id) {
            //add new vdoLiveSetTopic to vdoLiveSetTopics list
            this.classRoom.classSection.classAssignments.push(result);

          } else {
            //update new vdoLiveSetTopic to vdoLiveSetTopics list
            const index = this.classRoom.classSection.classAssignments.findIndex(item => {
              return item.id === result.id
            });
            this.classRoom.classSection.classAssignments[index] = result;

          }
          this.showEditForm = false;
          this.form.id = result.id;
          // this.form.classAssignmentInput.uploadPath = result.uploadPath;
          // this.form.classAssignmentInput.uploadPathUri = result.uploadPathUri;
          // this.form.classAssignmentInput.uploadFileName = result.uploadFileName;

        }
      },
      async removeClassAssignment(payload){
        //check student assignment before delete
        const canDelete = await this.checkCanDelete(payload);
        if(canDelete && canDelete.length > 0) {
          showLocalNotification(this.$vs, this.$t('general.warning')
            , this.$t('class-assignment.can-not-delete')
            , notificationType.WARNING);
        } else {
          let result = await this.deleteClassAssignment(payload);

          if(result){
            //remove list
            const index = this.classRoom.classSection.classAssignments.findIndex(item => item.id === result.id);
            this.classRoom.classSection.classAssignments.splice(index, 1);

            this.showEditForm = false;
          }
        }
      },
      formatDate(date){
        return moment(date).format(dateTimeFormat.momentYYYYMMDD)
      }
    },
  }
</script>

<style scoped>

</style>
